'use client';

import { createContext, useContext, ReactNode, useEffect, useState } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import type { PLPLocationEntry } from '@/lib/services/blob/types';
import {
  writeHeaderLocation,
  clearHeaderLocation,
  setHeaderLocationLoading,
} from '@/lib/utils/headerLocationStorage';

interface PLPLocalizationProviderProps {
  children: ReactNode;
}

const PaidLandingPageProvider = createContext<{
  locationData: PLPLocationEntry | null;
  isLocationLoading: boolean;
  isPaidLandingPage: boolean;
} | null>(null);

export const PLPLocalizationProvider = ({ children }: PLPLocalizationProviderProps) => {
  const [locationData, setLocationData] = useState<PLPLocationEntry | null>(null);
  const [isLocationLoading, setIsLocationLoading] = useState(true);
  const router = useRouter();

  useEffect(() => {
    if (!window) {
      setLocationData(null);
      setIsLocationLoading(false);
      return;
    }

    const ac = new AbortController();
    const fetchAndSyncLocalization = async () => {
      const brandCode = process.env.NEXT_PUBLIC_BRAND?.toUpperCase();
      if (!brandCode) return;

      const resetLocalization = () => {
        clearHeaderLocation(brandCode);
        setLocationData(null);
        setIsLocationLoading(false);
        setHeaderLocationLoading(false);
      };

      const searchParams = new URLSearchParams(window.location.search);
      const physical = searchParams.get('physical');

      if (!physical) {
        setIsLocationLoading(false);
        setHeaderLocationLoading(false);
        return resetLocalization();
      }

      try {
        const response = await fetch(`/api/plp-location?criteriaId=${physical}`, {
          signal: ac.signal,
        });

        if (!response.ok) {
          return resetLocalization();
        }

        const locationEntry = (await response.json()) as PLPLocationEntry;

        setLocationData(locationEntry);
        writeHeaderLocation(brandCode, {
          zip: '',
          locality: locationEntry.locality,
          stateShort: locationEntry.stateShort,
          link: '',
        });
      } catch (err: unknown) {
        const isAbortError = err instanceof Error && err.name === 'AbortError';

        if (!isAbortError) {
          resetLocalization();
        }
      } finally {
        setIsLocationLoading(false);
        setHeaderLocationLoading(false);
      }
    };

    fetchAndSyncLocalization();

    return () => {
      ac.abort();
    };
  }, []);

  return (
    <PaidLandingPageProvider.Provider
      value={{ locationData, isLocationLoading, isPaidLandingPage: true }}
    >
      {children}
    </PaidLandingPageProvider.Provider>
  );
};

export const usePaidLandingPageProvider = () => {
  const context = useContext(PaidLandingPageProvider);
  const pathname = usePathname();

  if (!context) {
    console.warn(
      `⚠️ WARNING: A PLP Component is being rendered on a non-PLP page: "${pathname}"\n` +
        `It is missing the PaidLandingPageProvider context. Gracefully falling back to generic content.`,
    );
    return { locationData: null, isLocationLoading: false, isPaidLandingPage: false };
  }
  return context;
};

export const useOptionalPaidLandingPageProvider = () => {
  return useContext(PaidLandingPageProvider);
};
